import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, finalize, tap } from "rxjs/operators";
import { CreateExportInterfaceActivationEmailTemplateDTO, ExportInterfaceActivationEmailTemplateClient, ExportInterfaceActivationEmailTemplateDTO, ExportInterfaceConfigurationTransferType, FilterDTO, FilterOperator, SortDirection, UpdateExportInterfaceActivationEmailTemplateDTO } from "src/app/base/models/models.swagger.generated";
import { ToastService } from "src/app/base/services/toast.service";
import { ShowInactiveService } from "src/app/base/shared/grid-base/show-inactive.service";
import { IAdaptorService } from "src/app/base/shared/syncfusion/adaptor-service.interface";
import { IPagedItems } from "src/app/base/shared/syncfusion/paged-items.interface";
import { SpinnerService } from "src/app/base/spinner/spinner.service";
import { ErrorType } from "src/app/core/routing/error-types";

@Injectable()
export class ActivationEmailTemplatesOverviewService extends ShowInactiveService implements IAdaptorService {
  public oneTimeFilterTemplateId: number;
  constructor(
    private activationEmailTempalteClient: ExportInterfaceActivationEmailTemplateClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService) {
    super();
  }

  public loadInitialData(): Observable<IPagedItems> {
    return this.getData(0, 0, [], "TemplateName", SortDirection.Ascending)
      .pipe(tap((pagedItems: IPagedItems) => {
        this._allActivationEmailTemplates = pagedItems.items;
      })
      );
  }

  private _allActivationEmailTemplates: ExportInterfaceActivationEmailTemplateDTO[];

  public getActivationEmailTemplatesForTransferType(transferType: ExportInterfaceConfigurationTransferType): ExportInterfaceActivationEmailTemplateDTO[] {
    return this._allActivationEmailTemplates.filter((t) => {
      return t.transferType == transferType;
    });
  }

  public getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<IPagedItems> {
    if (!sortBy) {
      sortBy = 'TemplateName';
    }
    this.spinnerService.show();
    if (!filters) {
      filters = [];
    }
    if (this.oneTimeFilterTemplateId) {
      filters.push(new FilterDTO({ field: 'id', operator: FilterOperator.Equal, value: this.oneTimeFilterTemplateId.toString(), isComplex: false }));
      this.oneTimeFilterTemplateId = null;
    }
    return this.activationEmailTempalteClient.searchExportInterfaceActivationEmailTemplates(skip, take, sortBy, sortDirection, false, filters)
      .pipe(finalize(() => {
        this.spinnerService.hide();
      }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        }));
  }

  public addRecord(): Observable<ExportInterfaceActivationEmailTemplateDTO> {
    let createTemplate = new CreateExportInterfaceActivationEmailTemplateDTO({});
    return this.activationEmailTempalteClient.createExportInterfaceActivationEmailTemplate(createTemplate)
      .pipe(
        tap((activationEmailTemplate: ExportInterfaceActivationEmailTemplateDTO) => {
          this._allActivationEmailTemplates.push(activationEmailTemplate);
          this.toastService.showSuccess(
            'i18n.domain.export-configuration.activation-email-templates.messages.create.success-title',
            'i18n.domain.export-configuration.activation-email-templates.messages.create.success-message',
          );
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        }),
      );
  }

  public updateRecord(updateTemplate: UpdateExportInterfaceActivationEmailTemplateDTO): Observable<ExportInterfaceActivationEmailTemplateDTO> {
    return this.activationEmailTempalteClient.updateExportInterfaceActivationEmailTemplate(updateTemplate)
      .pipe(
        tap((activationEmailTemplate: ExportInterfaceActivationEmailTemplateDTO) => {
          let itemIndex = this._allActivationEmailTemplates.findIndex(item => item.id == activationEmailTemplate.id);
          this._allActivationEmailTemplates[itemIndex] = activationEmailTemplate;
          this.toastService.showSuccess(
            'i18n.domain.export-configuration.activation-email-templates.messages.update.success-title',
            'i18n.domain.export-configuration.activation-email-templates.messages.update.success-message',
          );
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError, error.response);
          }
          return throwError(() => error);
        }),
      );
  }

  public deleteRecord(id: number) {
    return this.activationEmailTempalteClient.deleteExportInterfaceActivationEmailTemplate(id)
      .pipe(
        tap(() => {
          let itemIndex = this._allActivationEmailTemplates.findIndex(item => item.id == id);
          this._allActivationEmailTemplates.splice(itemIndex, 1);
          this.toastService.showSuccess(
            'i18n.domain.export-configuration.activation-email-templates.messages.delete.success-title',
            'i18n.domain.export-configuration.activation-email-templates.messages.delete.success-message',
          );
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showApiException(error, 'i18n.errors.server-error.title', 'i18n.errors.server-error.message');
          }
          return throwError(() => error);
        }),
      );
  }
}
